/* BROOT art layer — scroll engine. Vanilla, no dependencies.
   Native scrolling only: no wheel interception, no lerped scroll hijack.
   Progressive enhancement: without this file everything is still readable.

   19 Sep 2026 — rebuilt for phones. What changed and why:
     - The chapter wash used to be a CSS mask-image gradient rewritten on
       every scroll frame. A new mask on a full-screen layer is a full-screen
       repaint, sixty times a second, on a phone. The wash is now a solid
       block moved with transform, which the compositor handles alone.
     - `--vel` (scroll velocity) was written to <html> on every frame and
       drove a grain overlay's opacity. A custom property on the root
       invalidates style for the whole document. Gone, along with the loop
       that kept running frames after the finger had lifted.
     - Scenes only do work while they are within half a screen of the
       viewport; everything else is skipped without measuring it.
     - Nothing here writes a filter, a mask or a background. Only transform,
       opacity and class names.

   20 Sep 2026 — second pass, for the "third page is laggy" report:
     - Scene geometry (top, height) is measured once, and again only when
       the page changes size. A frame no longer calls getBoundingClientRect
       on every scene between style writes, which forced a synchronous
       layout per scene per frame.
     - The lists a scene animates (lines, frames, dots) are collected once.
     - Where the browser supports scroll-driven animations (Chrome 115+,
       Safari 26+), the wash block is moved by CSS on the compositor thread
       (broot-fix.css, section 5c) and this file never touches its transform.
       Class toggles for the type colour still come from here.
*/
(function () {
  if (window.__brootArt) return; window.__brootArt = true;

  var R = document.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var small = function () { return window.innerWidth < 768; };
  R.classList.add('ar-js');
  R.classList.remove('ar-nojs');

  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };

  /* ---------- 1. Chrome measurements ---------- */
  function measureHeader() {
    var h = document.querySelector('.shopify-section-header') || document.querySelector('header');
    if (h) R.style.setProperty('--bx-header', Math.round(h.getBoundingClientRect().height) + 'px');
  }

  /* ---------- 1b. Header state ----------
     `bx-over`  this page opens on a photograph, so the header floats over it
     `bx-menu`  a header drawer / search / account panel is open
     Plain classes on <html>: a selector that older mobile Safari cannot
     parse is thrown away with its whole rule, a class cannot fail that way. */
  function headerState() {
    if (!document.body) return;
    var isIndex = document.body.classList.contains('body-template-index');
    var photoHero = !!document.querySelector('.bx-page > .bx-hero:first-child');
    R.classList.toggle('bx-over', isIndex || photoHero);
  }

  function menuState() {
    R.classList.toggle('bx-menu', !!document.querySelector('.header-wrapper details[open]'));
  }

  function watchMenu() {
    var w = document.querySelector('.header-wrapper');
    if (!w || w.__brootWatched) return;
    w.__brootWatched = true;
    menuState();
    if (window.MutationObserver) {
      new MutationObserver(menuState).observe(w, {
        attributes: true, attributeFilter: ['open'], subtree: true
      });
    }
    w.addEventListener('click', function () { setTimeout(menuState, 0); });
  }

  /* ---------- 2. Masked line splitting ---------- */
  function splitLines(el) {
    if (el.dataset.arSplit) return; el.dataset.arSplit = '1';
    var words = (el.textContent || '').trim().split(/\s+/);
    if (!words.length) return;
    el.textContent = '';
    words.forEach(function (w, i) {
      var s = document.createElement('span');
      s.textContent = w + (i < words.length - 1 ? ' ' : '');
      el.appendChild(s);
    });
    var kids = Array.prototype.slice.call(el.children);
    var lines = [], cur = [], top = null;
    kids.forEach(function (k) {
      var t = k.offsetTop;
      if (top === null) top = t;
      if (Math.abs(t - top) > 3) { lines.push(cur); cur = []; top = t; }
      cur.push(k);
    });
    if (cur.length) lines.push(cur);
    el.textContent = '';
    lines.forEach(function (ln) {
      var o = document.createElement('span'); o.className = 'ar-ln';
      var i2 = document.createElement('span');
      i2.textContent = ln.map(function (k) { return k.textContent; }).join('');
      o.appendChild(i2); el.appendChild(o);
    });
  }

  /* ---------- 3. Reveals ---------- */
  var io = null;
  function initReveal(root) {
    var els = (root || document).querySelectorAll('.ar-rv:not(.is-in), .ar-lines:not(.is-in)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, function (e) { e.classList.add('is-in'); }); return;
    }
    if (!io) {
      io = new IntersectionObserver(function (en) {
        en.forEach(function (x) { if (x.isIntersecting) { x.target.classList.add('is-in'); io.unobserve(x.target); } });
      }, { threshold: 0.08, rootMargin: '0px 0px -4% 0px' });
    }
    Array.prototype.forEach.call(els, function (e) { io.observe(e); });
  }
  function sweepReveal() {
    var vh = window.innerHeight;
    document.querySelectorAll('.ar-rv:not(.is-in), .ar-lines:not(.is-in)').forEach(function (e) {
      var r = e.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) e.classList.add('is-in');
    });
  }

  /* ---------- 4. Scene engine ---------- */
  var scenes = [];
  var sda = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline: view()'));
  function collectScenes() {
    scenes = Array.prototype.slice.call(document.querySelectorAll('[data-ar-scene]')).map(function (el) {
      return { el: el, kind: el.getAttribute('data-ar-scene'), last: -1, lastLight: null, lastDone: null, lastWash: -1, arcInit: false, arcLen: 0,
        wash: el.querySelector('.ar-wash i'),
        lines: el.querySelectorAll('.ar-stack__l'),
        fr: el.querySelectorAll('.ar-reel__f'), cp: el.querySelectorAll('.ar-reel__c'), dt: el.querySelectorAll('.ar-dots b'),
        rail: el.querySelector('.ar-rail'),
        top: 0, h: 0, cssWash: false };
    });
    /* the CSS scroll-driven rise is only trusted once the browser has
       actually attached it; otherwise this file moves the wash itself */
    scenes.forEach(function (s) {
      s.cssWash = !!(sda && s.wash && getComputedStyle(s.wash).animationName === 'ar-wash-rise');
    });
    measureScenes();
  }
  /* one layout read per scene, outside the scroll loop */
  function measureScenes() {
    var y = window.scrollY || 0;
    for (var i = 0; i < scenes.length; i++) {
      var r = scenes[i].el.getBoundingClientRect();
      scenes[i].top = r.top + y; scenes[i].h = r.height;
    }
  }

  function setIndex(list, i) {
    for (var n = 0; n < list.length; n++) {
      list[n].classList.toggle('is-on', n === i);
      list[n].classList.toggle('is-out', n < i);
    }
  }

  function runScene(s, vh, y) {
    var top = s.top - y, bottom = top + s.h;
    var span = s.h - vh;
    if (span <= 0) return;
    /* out of range: leave it exactly as it was */
    if (bottom < -vh * 0.5 || top > vh * 1.5) return;
    var p = clamp(-top / span, 0, 1);

    if (s.kind === 'wash') {
      /* The white block rises from below the panel and has covered it by
         p = 0.9, leaving a beat of stillness on white before the pin ends.
         One transform write, and only when it has moved a visible amount. */
      if (s.wash && !s.cssWash) {
        var q = clamp(p / 0.9, 0, 1);
        var ty = Math.round((1 - q) * 1340) / 10;
        if (ty !== s.lastWash) {
          s.wash.style.transform = 'translate3d(0,' + ty + '%,0)';
          s.lastWash = ty;
        }
      }
      /* once the wash has risen behind the type, the type flips to black;
         the kicker near the top waits until the wash is nearly done */
      var lit = p > 0.5;
      if (lit !== s.lastLight) { s.el.classList.toggle('ar-on-light', lit); s.lastLight = lit; }
      var done = p > 0.84;
      if (done !== s.lastDone) { s.el.classList.toggle('ar-wash-done', done); s.lastDone = done; }
    }

    if (s.kind === 'stack' || s.kind === 'wash') {
      var lines = s.lines;
      if (lines.length) {
        /* the lines change over the first 85% of the pin, so the last line is
           already settled when the wash finishes underneath it */
        var lp = clamp(p / 0.85, 0, 1);
        var i = clamp(Math.floor(lp * lines.length * 0.999), 0, lines.length - 1);
        if (i !== s.last) { setIndex(lines, i); s.last = i; }
      }
    }

    if (s.kind === 'reel') {
      var fr = s.fr, cp = s.cp, dt = s.dt;
      var n2 = fr.length || cp.length;
      if (n2) {
        var j = clamp(Math.floor(p * n2 * 0.999), 0, n2 - 1);
        if (j !== s.last) {
          for (var k = 0; k < n2; k++) {
            if (fr[k]) fr[k].classList.toggle('is-on', k === j);
            if (cp[k]) cp[k].classList.toggle('is-on', k === j);
            if (dt[k]) dt[k].classList.toggle('is-on', k === j);
          }
          s.last = j;
        }
      }
    }

    if (s.kind === 'rail') {
      var rail = s.rail;
      if (rail && rail.parentElement) {
        if (!s.railDist) s.railDist = rail.scrollWidth - rail.parentElement.clientWidth;
        if (s.railDist > 0) rail.style.transform = 'translate3d(' + (-p * s.railDist).toFixed(1) + 'px,0,0)';
      }
    }

    if (s.kind === 'arc') {
      var track = s.el.querySelector('.ar-arc .track');
      var prog = s.el.querySelector('.ar-arc .prog');
      var nodes = s.el.querySelectorAll('.ar-arc .node');
      var words = s.el.querySelectorAll('.ar-stage');
      var ticks = s.el.querySelectorAll('.ar-tick');
      if (track && !s.arcInit && track.getTotalLength) {
        var L = track.getTotalLength();
        s.arcLen = L; s.arcInit = true;
        if (prog) { prog.style.strokeDasharray = L; prog.style.strokeDashoffset = L; }
        for (var z = 0; z < nodes.length; z++) {
          var at = nodes.length === 1 ? L / 2 : (L * z) / (nodes.length - 1);
          var pt = track.getPointAtLength(at);
          nodes[z].setAttribute('cx', pt.x.toFixed(2));
          nodes[z].setAttribute('cy', pt.y.toFixed(2));
        }
      }
      if (prog && s.arcLen) prog.style.strokeDashoffset = (s.arcLen * (1 - p)).toFixed(2);
      var cnt = words.length || nodes.length;
      if (cnt) {
        var q2 = clamp(Math.floor(p * cnt * 0.999), 0, cnt - 1);
        if (q2 !== s.last) {
          setIndex(words, q2);
          for (var y = 0; y < cnt; y++) {
            if (ticks[y]) ticks[y].classList.toggle('is-on', y === q2);
            if (nodes[y]) {
              nodes[y].setAttribute('r', y === q2 ? '4.2' : '2');
              nodes[y].setAttribute('fill', y <= q2 ? '#F4EFE6' : 'rgba(244,239,230,.26)');
            }
          }
          s.last = q2;
        }
      }
    }
  }

  /* ---------- 5. Parallax (desktop only) ---------- */
  var px = [];
  function collectPx() { px = Array.prototype.slice.call(document.querySelectorAll('[data-ar-px]')); }
  function readPx() {
    if (reduce || small() || !px.length) return null;
    return px.map(function (img) { var host = img.parentElement; return host ? host.getBoundingClientRect() : null; });
  }
  function runPx(vh, rects) {
    if (!rects) return;
    px.forEach(function (img, i) {
      var r = rects[i]; if (!r) return;
      if (r.bottom < -60 || r.top > vh + 60) return;
      var q = (r.top + r.height / 2 - vh / 2) / vh;
      var amt = parseFloat(img.getAttribute('data-ar-px')) || 0;
      img.style.transform = 'translate3d(0,' + (q * amt).toFixed(2) + 'px,0)';
    });
  }

  /* ---------- 6. Loop: one frame per scroll event, nothing after ----------
     All layout reads happen first (parallax hosts; scenes use cached
     geometry), then all writes, so the browser lays out at most once. */
  var queued = false, lastScrolled = null;
  function frame() {
    queued = false;
    var vh = window.innerHeight;
    var y = window.scrollY || 0;
    var pxr = readPx();

    var sc = y > 40;
    if (sc !== lastScrolled) { R.classList.toggle('is-scrolled', sc); lastScrolled = sc; }

    for (var i = 0; i < scenes.length; i++) runScene(scenes[i], vh, y);
    runPx(vh, pxr);
  }
  function onScroll() { if (!queued) { queued = true; requestAnimationFrame(frame); } }

  function boot(root) {
    document.querySelectorAll('.ar-lines:not([data-ar-split])').forEach(splitLines);
    headerState();
    collectScenes(); collectPx(); initReveal(root); measureHeader(); frame();
  }

  function start() {
    boot(document);
    watchMenu();
    /* If this boot is late (slow network), broot-fix.css has already shown
       everything through its 2.6s safety animation. Mark what is on screen
       as revealed BEFORE announcing readiness, so nothing blinks off. */
    var late = (window.performance && performance.now && performance.now() > 2400);
    if (late) sweepReveal();
    R.classList.add('ar-ready');
    setTimeout(function () { boot(document); watchMenu(); sweepReveal(); frame(); }, 900);
    /* and anything still hidden after 2.5s is shown */
    setTimeout(sweepReveal, 2500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.addEventListener('load', function () { headerState(); watchMenu(); collectScenes(); collectPx(); measureHeader(); frame(); });
  if (window.ResizeObserver) {
    var roQueued = false;
    new ResizeObserver(function () {
      if (roQueued) return; roQueued = true;
      requestAnimationFrame(function () { roQueued = false; measureScenes(); frame(); });
    }).observe(document.documentElement);
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () { collectScenes(); collectPx(); measureHeader(); frame(); }, { passive: true });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) { sweepReveal(); frame(); } });
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); watchMenu(); frame(); });
})();
